<template>
	<div class="encrypted form-group" :class="{ 'is-empty': empty, 'is-disabled': readonly }" :data-element="element.id"
		:title="readonly ? $t('app.common.readonly') : ''">
		<div class="d-flex justify-content-between align-items-center">
			<label class="element-label" :class="classes('title')" :title="required ? trans('buttons.required') : ''">
				<span v-html="macro(label)"></span>
				<small v-if="required" class="required">*</small>
			</label>
			<div v-if="fieldVisibleOnlyInPreview" class="app--engagement-advisor-preview-icon ms-2">
				<app-icon :title="trans('visibility.field.preview')" value="eye" type="fas" class="fa-sm" />
			</div>
		</div>
		<div class="input-group" ref="field">
			<input type="text" class="form-control" v-model="value" @blur="update" @focus="focus"
				:class="classes('input')" :data-label="label" :maxlength="options('max')" :minlength="options('min')"
				:readonly="readonly || disabled" :required="required">
			<div class="input-group-text" :class="classes('input')">
				<app-icon value="lock"></app-icon>
			</div>
		</div>
		<div class="invalid-feedback" :for="label" :class="{ 'd-block': !valid }">
			<span v-if="!valid">{{ $t('validation.mandatory') }}</span>
		</div>
		<small class="form-text" :class="classes('description')">
			<span v-if="description" v-html="macro(description)"></span>&nbsp;
		</small>
		<div v-show="popover.active" ref="popover" class="app--popup popper" role="tooltip" width="200">
			<div class="p-content encrypted">
				<p :class="{ 'text-danger text-left': response.error }">{{ response.message }}</p>
				<div class="form-group">
					<field-password class="py-3" v-model:value="password" :placeholder="$t('app.confirm.password')"
						:classes="classes('input') + ' no-validate'" v-on:keyenter="proceed" :validate-minlength="0"
						:validate-strength="false" :validate-caps="false">
						<template v-slot:prepend>
							<div class="input-group-prepend">
								<span class="input-group-text" :class="classes('input')"><app-icon
										value="lock"></app-icon></span>
								<input type="email" name="email" autocomplete="off" class="d-none no-validate" />
							</div>
						</template>
					</field-password>
					<div class="d-flex align-items-center justify-content-between">
						<app-button class="primary" :disabled="password == ''" :loading="loading"
							:label="$t('app.confirm.proceed')" @click.native="proceed"></app-button>
						<app-button class="muted" :disabled="loading" :label="$t('app.common.cancel')"
							@click.native="cancel"></app-button>
					</div>
				</div>
			</div>
			<div class="p-arrow" data-popper-arrow></div>
		</div>
	</div>
</template>

<script>
import store from '../../store'
import { createPopper } from '@popperjs/core'
import Password from '../../../components/Password'

export default {
	props: {
		definition: { type: Object, required: true },
		element: { type: Object, required: true },
	},
	computed: {
		complete() {
			return store.state.complete === true
		},
		description() {
			return typeof this.definition.description == 'string' ? this.definition.description : null
		},
		disabled() {
			return this.preview && this.complete && this.locked
		},
		empty() {
			return this.original == ''
		},
		label() {
			return this.definition.title
		},
		preview() {
			return store.state.preview === true
		},
		readonly() {
			return store.getters.readonly(this.definition, !this.empty)
		},
		required() {
			return this.options('required') == true
		},
		valid() {
			return !(this.element && this.element.valid === false)
		},
		fieldVisibleOnlyInPreview() {
			return this.definition.print_visibility == 'preview' && store.state.preview == true && store.state.complete == false
		}
	},
	data() {
		return {
			value: '',
			original: '',
			password: '',
			response: { message: this.$t('app.confirm.message'), error: false },
			loading: false,
			locked: true,
			popover: { el: null, active: false },
		}
	},
	watch: {
		'element.value': {
			immediate: true,
			handler(value) {
				this.value = value && typeof value == 'string' ? value.toString() : ''
				this.original = this.value
				if (this.value == '' && this.locked == true) {
					this.locked = false
				}
			}
		}
	},
	mounted() {
		if (this.preview) {
			this.popover.el = createPopper(this.$refs.field, this.$refs.popover, {
				placement: 'top',
				modifiers: [
					{ name: 'flip', options: { fallbackPlacements: ['top', 'bottom'] } },
					{ name: 'offset', options: { offset: [0, 8] } },
					{ name: 'arrow', options: { padding: 5 } },
				]
			})
		}
	},
	methods: {
		classes(index) {
			return store.getters['classes'](index)
		},
		cancel(event) {
			this.popover.active = false
			this.popover.el.update()
			this.locked = true
			this.password = ''
			this.value = this.original
		},
		focus() {
			if (!this.preview) {
				this.value = ''
			}
			if (this.preview && this.locked) {
				this.response = { message: this.$t('app.confirm.message'), error: false }
				this.popover.active = true
				this.popover.el.update()
			}
		},
		macro(value) {
			return store.getters['macro'](value)
		},
		options(value) {
			return store.getters['options'](this.definition, value)
		},
		trans(value) {
			let trans = store.getters.trans(value);
			return trans ? trans : this.$t('front.' + value)
		},
		proceed() {
			this.loading = true
			let payload = { id: this.element.id, value: this.value, password: this.password }
			store.dispatch('unlock', payload).then(response => {
				this.loading = false
				if (response.value && !response.error) {
					this.popover.active = false
					this.locked = false
					this.password = ''
					this.original = response.value
					this.value = response.value
				} else {
					this.popover.active = true
					this.locked = true
					this.password = ''
					this.response.message = response.value
					this.response.error = true
				}
			})
		},
		update(event) {
			if (this.preview && this.complete) {
				return null
			}
			if (this.preview) {
				if (!this.readonly && this.$validate(event) && event.target.value !== this.element.value) {
					let gk = this.element.group + '|' + this.definition.group
					store.commit('QUEUE', { id: this.element.id, group: gk, value: this.value })
				}
			} else {
				if (!event.target.value && this.original) {
					this.value = this.original
					event.target.value = this.original
				} else if (!this.readonly && this.$validate(event) && event.target.value != this.element.value) {
					let gk = this.element.group + '|' + this.definition.group
					store.commit('QUEUE', { id: this.element.id, group: gk, value: this.value })
				}
			}
		},
	},
	components: {
		'field-password': Password
	}
}
</script>
